import { JSX } from "react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { CategoricalChartState } from "recharts/types/chart/types";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { ProvidersByNetworkData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { colors, tickFormatter } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { DashboardTile } from "@app/shared/qes-standards";
import { printNumberAsString } from "@app/shared/waffles";

interface ProvidersByNetworkProps {
  canDrillIn: boolean;
  customTooltip: (
    props: TooltipProps<ValueType, string | number>,
    titleKey: string,
  ) => JSX.Element;
  customYAxisTick?: ({ x, y, payload }: { x: number, y: number, payload: { value: string } }) => JSX.Element;
  data: ProvidersByNetworkData[]
  loading: boolean;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    isGhost?: boolean,
  ) => void;
  selectedNetwork: string;
}

const ProvidersByNetwork = (props: ProvidersByNetworkProps): JSX.Element => {
  const onProvidersByNetworkClick = (data: CategoricalChartState): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!data && props.canDrillIn) {
      const contractData: ProvidersByNetworkData = props.data.find(x => x.contractId === data.activeLabel);

      if (!!contractData?.providerCount) {
        props.onDashboardDetailsViewClick(BenchmarkingDashboardDetailsTypeEnum.NetworkDrillin, contractData?.providerCount, contractData?.versionId);
      }
    }
  }

  return (
    <DashboardTile
      loading={props.loading}
      subtitle={props.canDrillIn && "Each bar can be clicked to reveal additional details."}
      title="Providers By Network"
      widthClass="col-md-12"
    >
      <div style={{ height: "300px", width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            barCategoryGap="20%"
            data={props.data}
            layout="vertical"
            margin={{
              bottom: 0,
              left: 0,
              right: 50, // formatting to prevent the number displayed to right of the longest bar from truncating
              top: 20,
            }}
            onClick={(data: CategoricalChartState) => onProvidersByNetworkClick(data)}
          >
            <CartesianGrid strokeDasharray="1 0" horizontal={false} />
            <XAxis type="number" dataKey="providerCount" tickLine={false} tickFormatter={tickFormatter} />
            <YAxis dataKey="contractId" type="category" tickLine={false} tick={props.customYAxisTick} />
            <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId")} />
            <Bar dataKey="providerCount">
              <LabelList
                formatter={(providerCount: number) => printNumberAsString(providerCount, "0")}
                dataKey="providerCount"
                position="right" // adding the provider count to the side of a bar
                fill="#000000"
              />
              {props.data.map((entry, index) => (
                // custom bar chart colors
                <Cell id={`benchmarking-dashboard__providers-by-network--contract-${entry.contractId}-count-${entry.providerCount}`} key={`cell-${index}`} fill={entry.contractId === props.selectedNetwork ? colors.green : colors.lightBlue} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardTile>
  )
}

export default ProvidersByNetwork;
